import { AlertTriangle, CheckCircle, RefreshCw } from "lucide-react";
import { Card, PageHeader } from "../../components/ui/patrones";
import { Aviso, Boton, Etiqueta } from "../../components/ui/primitivos";
import { type EstadoSistema, fetchEstadoSistema } from "../../lib/consola-api";
import { useDatos } from "../../lib/useDatos";

/**
 * Estado del sistema — lo que sobrevive de `SystemMonitoringPage` de `imprenta`
 * (ADR 0032).
 *
 * Allá son cuatro pestañas y acá una sola pantalla, porque tres de esas cuatro
 * no tienen qué mostrar sobre Workers: «Recursos» sale de `psutil` sobre un
 * servidor propio, «Métricas» la cubre la observabilidad de Cloudflare con más
 * detalle, y «Logs» —esto es lo que hay que mirar dos veces— devuelve
 * `Sample log message {i}` generado en un bucle: es un placeholder, no una
 * funcionalidad.
 *
 * Lo que queda es la pregunta que sí importa acá y no importaba allá: **¿está
 * todo enchufado?** Con las piezas aprovisionándose de a una (ADR 0009), R2 o
 * KV pueden faltar, y sin esta pantalla eso se descubre por un arte que no
 * sube.
 */
export function EstadoSistemaPagina() {
  const estado = useDatos(fetchEstadoSistema);
  const datos = estado.datos;

  const faltantes = datos?.piezas.filter((p) => !p.presente) ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Estado del sistema"
        subtitulo="Qué está enchufado y qué deja de funcionar si no lo está"
        acciones={
          <Boton
            variante="secundario"
            className="inline-flex items-center"
            onClick={() => void estado.recargar()}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Actualizar
          </Boton>
        }
      />

      {estado.error ? <Aviso>{estado.error}</Aviso> : null}

      {estado.cargando && !datos ? (
        // Centrado y con alto propio, como allá: un renglón chico contra el
        // borde no se lee como «está cargando», se lee como «no hay nada».
        <div className="flex h-64 items-center justify-center text-neutral-500">Cargando...</div>
      ) : null}

      {datos ? (
        <>
          {faltantes.length > 0 ? (
            <Aviso>
              {faltantes.length === 1
                ? `Falta ${faltantes[0]?.nombre}.`
                : `Faltan ${faltantes.length} piezas.`}{" "}
              Abajo está qué deja de funcionar con cada una.
            </Aviso>
          ) : null}

          <Card className="p-6">
            <h2 className="font-semibold text-neutral-900 text-sm">Entorno</h2>
            <div className="mt-3">
              <Etiqueta tono={datos.entorno === "prod" ? "error" : "neutral"}>
                {datos.entorno}
              </Etiqueta>
            </div>
            <p className="mt-2 text-neutral-500 text-xs">
              {datos.entorno === "dev"
                ? "Local. El transporte de email simulado solo existe acá (ADR 0020)."
                : "Los secretos van por wrangler, no por GitHub (ADR 0009)."}
            </p>
          </Card>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {datos.piezas.map((pieza) => (
              <Card key={pieza.nombre} className="p-6">
                <div className="flex items-start gap-3">
                  {pieza.presente ? (
                    <CheckCircle
                      aria-hidden="true"
                      className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600"
                    />
                  ) : (
                    <AlertTriangle
                      aria-hidden="true"
                      className="mt-0.5 h-5 w-5 shrink-0 text-amber-500"
                    />
                  )}
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-neutral-900 text-sm">{pieza.nombre}</span>
                      {/* El icono y el color no alcanzan: quien no los
                          distingue tiene que poder leer el estado. */}
                      <Etiqueta tono={pieza.presente ? "ok" : "aviso"}>
                        {pieza.presente ? "conectado" : "falta"}
                      </Etiqueta>
                    </div>
                    <p className="mt-1 text-neutral-500 text-xs">{pieza.detalle}</p>
                    {/* El «Remitente:» de la tarjeta SMTP de `imprenta`. Es lo
                        que se mira cuando alguien reporta que no le llegó un
                        mail, y sin eso la pantalla dice que el email anda sin
                        decir desde dónde sale. */}
                    {pieza.datos?.map((dato) => (
                      <p key={dato.etiqueta} className="mt-1 text-neutral-600 text-xs">
                        {dato.etiqueta}: <span className="text-neutral-900">{dato.valor}</span>
                      </p>
                    ))}
                    {/* Lo que se pierde, no el binding que falta: «sin R2» no
                        le dice nada a quien administra; «no se puede subir
                        arte» sí. */}
                    {pieza.sinEsto ? (
                      <p className="mt-2 text-amber-700 text-xs">Sin esto: {pieza.sinEsto}</p>
                    ) : null}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <TarjetaOutbox outbox={datos.outbox} />

          <Card className="p-6">
            <h2 className="font-semibold text-neutral-900 text-sm">Auditoría</h2>
            <div className="mt-2 font-bold text-2xl text-neutral-900">
              {datos.auditoria.eventos.toLocaleString("es-AR")}
            </div>
            <p className="mt-1 text-neutral-500 text-xs">
              Eventos registrados. La auditoría no es apagable (ADR 0025).
            </p>
          </Card>

          <Card className="p-6">
            <h2 className="font-semibold text-neutral-900 text-sm">Funcionalidades</h2>
            {/* En `imprenta` estos badges son tres constantes de config que
                nadie puede cambiar. Acá son los flags reales, y el que quiera
                tocarlos va a /flags. */}
            <div className="mt-3 flex flex-wrap gap-2">
              {datos.funcionalidades.reglas.map((regla) => (
                <Etiqueta key={regla.clave} tono={regla.habilitada ? "ok" : "aviso"}>
                  {regla.clave}: {regla.habilitada ? "activa" : "apagada"}
                </Etiqueta>
              ))}
            </div>
            <h3 className="mt-5 font-medium text-neutral-500 text-xs">Tipos de alerta</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {datos.funcionalidades.tiposDeAlerta.map((tipo) => (
                <Etiqueta key={tipo}>{tipo}</Etiqueta>
              ))}
            </div>
          </Card>
        </>
      ) : null}
    </div>
  );
}

/**
 * El outbox como termómetro del email.
 *
 * Una fila `fallido` es terminal (ADR 0005): nadie la va a reintentar sola, y
 * del otro lado hay alguien esperando una invitación que no llegó. Por eso se
 * destaca en rojo aunque las otras dos columnas estén bien.
 */
function TarjetaOutbox({ outbox }: { outbox: EstadoSistema["outbox"] }) {
  return (
    <Card className="p-6">
      <h2 className="font-semibold text-neutral-900 text-sm">Outbox de email</h2>
      <div className="mt-4 flex flex-wrap gap-8">
        <div>
          <div className="font-bold text-2xl text-neutral-900">{outbox.enviados}</div>
          <div className="text-neutral-500 text-xs">Enviados</div>
        </div>
        <div>
          <div className="font-bold text-2xl text-neutral-900">{outbox.pendientes}</div>
          <div className="text-neutral-500 text-xs">Pendientes</div>
        </div>
        <div>
          <div
            className={`font-bold text-2xl ${outbox.fallidos > 0 ? "text-red-600" : "text-neutral-900"}`}
          >
            {outbox.fallidos}
          </div>
          <div className="text-neutral-500 text-xs">Fallidos</div>
        </div>
      </div>
      {outbox.fallidos > 0 ? (
        <p className="mt-3 text-red-700 text-xs">
          Un envío fallido no se reintenta solo: el estado es terminal (ADR 0005). Hay que resolver
          la causa y volver a disparar la acción que originó el mail.
        </p>
      ) : null}
    </Card>
  );
}
